import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Pressable } from '../Common/Pressable'
import { useTimeline } from '../../context/useTimeline'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNavigation } from '../../context/useNavigation'
import type { DashboardViewProps } from '../DashboardContainer'
import type { DashboardChecklistVisualItem } from '../../types/dashboardVisualItem'
import type { TimelineElement } from '../../types/timelineElement'
import {
  compactLabel,
  elementEndMs,
  elementStartMs,
  formatElementRange,
  getElementColor,
  getHoverTitle,
  getLayoutTransition,
  splitMacroElements,
} from './viewUtils'

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const TAU = Math.PI * 2
type OrbitPoint = { angle: number; radius: number; x: number; y: number }

// Status-driven color for a goal's date-range arc on its ring. Reuses the
// standard tailwind hue set already used elsewhere in the app so it reads as
// "done / on track / not started / past-due" without a legend.
const getGoalRingStroke = (goal: TimelineElement, todayMs: number): string => {
  if (goal.boardStatus === 'COMPLETE') return 'var(--success)'
  const endMs = elementEndMs(goal)
  if (endMs > 0 && endMs < todayMs) return 'var(--warning)'
  if (goal.boardStatus === 'IN_PROGRESS') return 'var(--info)'
  if (goal.boardStatus === 'TODO') return 'var(--muted)'
  return 'var(--info)'
}

const getChecklistDateMs = (item: DashboardChecklistVisualItem) => {
  const raw = item.startDate ?? item.endDate
  if (!raw) return null
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

// Deterministic 0..1 hash so adding a checklist item does not reshuffle every
// other dot's scatter position.
const hashRatio = (id: string, salt = '') => {
  const seed = `${id}#${salt}`
  let hash = 2166136261
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 10000) / 10000
}

const arcPath = (cx: number, cy: number, r: number, startAngle: number, endAngle: number) => {
  const start = { x: cx + Math.cos(startAngle) * r, y: cy + Math.sin(startAngle) * r }
  const end = { x: cx + Math.cos(endAngle) * r, y: cy + Math.sin(endAngle) * r }
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

export const ConstellationView = ({
  elements,
  visualItems,
  viewStartMs,
  viewEndMs,
  selectedElementId,
  onElementClick,
  onChecklistClick,
  todayContext,
}: DashboardViewProps) => {
  const { calculateElementProgress } = useTimeline()
  const { preferences } = useClientPreferences()
  const { currentLevel } = useNavigation()
  const { goals, tasks, tasksByGoalId, subTasksByTaskId } = splitMacroElements(elements)
  const transition = getLayoutTransition(preferences.animationProfile)
  const checklistItems = visualItems.filter((item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST' && !item.isUnscheduled)
  const elementById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])

  const stageRef = useRef<HTMLDivElement | null>(null)
  const [stageDims, setStageDims] = useState({ width: 900, height: 440 })

  useLayoutEffect(() => {
    const node = stageRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    setStageDims({ width: rect.width || 900, height: rect.height || 440 })
    const observer = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      setStageDims({ width: cr.width || 900, height: cr.height || 440 })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const stageWidth = stageDims.width
  const baseStageHeight = currentLevel === 'dashboard' ? 440 : currentLevel === 'goal' ? 400 : 360
  const scale = clamp(stageWidth / 900, 0.72, 1.15)
  const [hoveredGoalId, setHoveredGoalId] = useState<string | null>(null)
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null)

  // ---- Scale-adaptive gates -------------------------------------------------
  // Canvas gates: react to actual viewport, not user preferences.
  const narrowStage = stageWidth > 0 && stageWidth < 640
  const shortStage = stageDims.height > 0 && stageDims.height < 350
  // Fill available room: whenever the container is bigger than the base
  // 900×460 canvas, grow orbit radii + sub-task gap so the constellation
  // spreads out and fills the stage instead of clustering near the top.
  const radiusMultiplier = clamp(
    Math.min(
      stageDims.height > 0 ? stageDims.height / 460 : 1,
      stageWidth > 0 ? stageWidth / 900 : 1,
    ),
    1,
    1.35,
  )
  const subGapExtra = (radiusMultiplier - 1) * 40

  // Density: element-count gate mirroring JourneyView's `sparseGoals` pattern.
  const totalTasks = tasks.length
  let maxTasksPerGoal = 0
  tasksByGoalId.forEach((list) => {
    if (list.length > maxTasksPerGoal) maxTasksPerGoal = list.length
  })
  let density: 'sparse' | 'normal' | 'dense' =
    goals.length <= 3 && maxTasksPerGoal <= 5
      ? 'sparse'
      : goals.length >= 6 || maxTasksPerGoal >= 8 || totalTasks >= 20
      ? 'dense'
      : 'normal'
  if (shortStage) density = 'dense'
  const sizeMult = density === 'sparse' ? 1.12 : density === 'dense' ? 0.85 : 1

  // Nav-level gates: progressively reveal detail as the user drills down.
  // Sub-tasks are always rendered so board view stays informative — we just
  // hide checklists at dashboard level to avoid dot-storms.
  const levelTiers = useMemo(() => {
    switch (currentLevel) {
      case 'dashboard':
        return { showSubTasks: true, showChecklists: false }
      case 'goal':
      case 'task':
      case 'subtask':
      default:
        return { showSubTasks: true, showChecklists: true }
    }
  }, [currentLevel])

  // Effective visibility (level + short-stage gate). Sub-tasks always render;
  // captions still drop in dense/short views to avoid label collisions.
  const showSubTasks = levelTiers.showSubTasks
  const showChecklists = levelTiers.showChecklists && !shortStage
  const subFanArc = (density === 'sparse' ? 0.9 : 0.72) + (radiusMultiplier - 1) * 0.35

  const centers = goals.map((_, index) => {
    if (goals.length === 1) return { x: 50, y: 52 }
    const columns = narrowStage ? 1 : Math.min(3, goals.length)
    const rows = Math.ceil(goals.length / columns)
    const column = index % columns
    const row = Math.floor(index / columns)
    const x = ((column + 1) / (columns + 1)) * 100
    const y = ((row + 1) / (rows + 1)) * 100
    return { x: clamp(x, 22, 78), y: clamp(y, 34, 66) }
  })

  const taskPointCache = useMemo(() => {
    const cache = new Map<string, OrbitPoint>()
    goals.forEach((goal) => {
      const goalTasks = tasksByGoalId.get(goal.id) ?? []
      const placed: OrbitPoint[] = []
      goalTasks.forEach((task, index) => {
        const timeRatio = (elementStartMs(task) - viewStartMs) / Math.max(1, viewEndMs - viewStartMs)
        const angle = TAU * (Number.isFinite(timeRatio) ? timeRatio : index / Math.max(1, goalTasks.length)) - Math.PI / 2
        const weightOffset = clamp(task.weight * 3, 0, 22)
        const priorityPull = calculateElementProgress(task.id) < 35 ? -12 : 0
        const baseRadius = (goals.length > 1 ? 74 : 94) + (index % 3) * 12 + weightOffset + priorityPull
        let radius = baseRadius * radiusMultiplier
        let x = Math.cos(angle) * radius
        let y = Math.sin(angle) * radius
        while (placed.some((point) => Math.hypot(point.x - x, point.y - y) < 68)) {
          radius += 14
          x = Math.cos(angle) * radius
          y = Math.sin(angle) * radius
        }
        const point = { angle, radius, x, y }
        placed.push(point)
        cache.set(task.id, point)
      })
    })
    return cache
  }, [calculateElementProgress, goals, radiusMultiplier, tasksByGoalId, viewEndMs, viewStartMs])

  // Per-goal, per-task priority rank in 0..1 (higher = more important). Drives
  // the outer-ring opacity + a small size bump so priority is discoverable at a
  // glance without adding new colors.
  const priorityRank = useMemo(() => {
    const map = new Map<string, number>()
    goals.forEach((goal) => {
      const siblings = tasksByGoalId.get(goal.id) ?? []
      const sorted = [...siblings].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
      const n = Math.max(1, sorted.length - 1)
      sorted.forEach((task, i) => map.set(task.id, sorted.length <= 1 ? 1 : 1 - i / n))
    })
    return map
  }, [goals, tasksByGoalId])

  // Task-blocker set: any task with invalid deps OR any checklist under it (or
  // under one of its subtasks) with a blocker.
  const blockedTaskIds = useMemo(() => {
    const set = new Set<string>()
    tasks.forEach((task) => {
      if ((task.invalidDependencyNodeIds?.length ?? 0) > 0) set.add(task.id)
    })
    visualItems.forEach((item) => {
      if (item.kind !== 'CHECKLIST') return
      const blockerCount = (item as DashboardChecklistVisualItem).blockerCount ?? 0
      if (blockerCount <= 0) return
      const directParent = item.parentId ? elementById.get(item.parentId) : null
      if (directParent?.type === 'TASK') set.add(directParent.id)
      else if (directParent?.type === 'SUBTASK' && directParent.parentId) set.add(directParent.parentId)
    })
    return set
  }, [elementById, tasks, visualItems])

  const goalSize = 84 * scale * sizeMult
  const taskSize = 48 * scale * sizeMult
  const subTaskSize = 24 * scale * sizeMult
  const checklistSize = 20 * scale * sizeMult
  const ringRadius = 66 * scale * sizeMult
  const goalFont = clamp(scale * sizeMult, 0.8, 1.15)
  const windowMs = Math.max(1, viewEndMs - viewStartMs)

  const labelReserveAbove = 60 * scale
  const topPadding = 16
  const bottomPadding = 20
  const columnsCount = goals.length === 1 ? 1 : narrowStage ? 1 : Math.min(3, goals.length)
  const rowsCount = Math.max(1, Math.ceil(goals.length / columnsCount))

  const taskCaptionReserve = 0

  // Per-goal vertical extents measured from the goal center (in px, scale-aware).
  // Includes the goal ring, the label card that floats above, every task /
  // sub-task fan-out, AND the always-visible task-title caption that sits just
  // beneath every task — so we can guarantee full visibility no matter how the
  // fan lands.
  const goalExtents = useMemo(() => {
    const map = new Map<string, { above: number; below: number }>()
    goals.forEach((goal) => {
      let above = goalSize / 2 + labelReserveAbove
      let below = goalSize / 2 + 12
      const goalTasks = tasksByGoalId.get(goal.id) ?? []
      goalTasks.forEach((task) => {
        const point = taskPointCache.get(task.id)
        if (!point) return
        const ty = point.y * scale
        above = Math.max(above, -ty + taskSize / 2 + 8)
        below = Math.max(below, ty + taskSize / 2 + taskCaptionReserve)
        if (!showSubTasks) return
        const subs = subTasksByTaskId.get(task.id) ?? []
        subs.forEach((_, subIndex) => {
          const subAngle = point.angle + ((subIndex + 1) / (subs.length + 1) - 0.5) * subFanArc
          const subRadius = (point.radius + 32 + subGapExtra) * scale
          const sy = Math.sin(subAngle) * subRadius
          above = Math.max(above, -sy + subTaskSize / 2 + 8)
          below = Math.max(below, sy + subTaskSize / 2 + 8)
        })
      })
      map.set(goal.id, { above, below })
    })
    return map
  }, [goals, goalSize, labelReserveAbove, scale, showSubTasks, subFanArc, subGapExtra, subTaskSize, subTasksByTaskId, taskCaptionReserve, taskPointCache, tasksByGoalId, taskSize])

  // Row-level max extents so goals in the same row line up at the same Y.
  const { rowAbove, rowBelow } = useMemo(() => {
    let above = 0
    let below = 0
    goalExtents.forEach((ext) => {
      if (ext.above > above) above = ext.above
      if (ext.below > below) below = ext.below
    })
    return { rowAbove: above, rowBelow: below }
  }, [goalExtents])

  const requiredHeight = (rowAbove + rowBelow) * rowsCount + topPadding + bottomPadding
  const stageHeight = Math.max(baseStageHeight, requiredHeight, stageDims.height || 0)

  const stagePx = useMemo(() => {
    const width = stageWidth || 900
    const height = stageHeight
    const goalPx = new Map<string, { x: number; y: number }>()
    const taskPx = new Map<string, { x: number; y: number; angle: number; radius: number }>()
    const subTaskPx = new Map<string, { x: number; y: number }>()

    const rowSlotHeight = (height - topPadding - bottomPadding) / rowsCount
    // Anchor goals near the top of each row's slot: leave just enough room for
    // the tallest above-fan (label + upward tasks/subtasks). Everything below
    // the goal has room to fan out into the remainder of the slot.
    const rowCenterY = (rowIndex: number) => topPadding + rowIndex * rowSlotHeight + rowAbove

    goals.forEach((goal, goalIndex) => {
      const center = centers[goalIndex]
      if (!center) return
      const rowIndex = Math.floor(goalIndex / columnsCount)
      const gy = rowCenterY(rowIndex)
      const gx = (center.x / 100) * width
      goalPx.set(goal.id, { x: gx, y: gy })

      const goalTasks = tasksByGoalId.get(goal.id) ?? []
      goalTasks.forEach((task) => {
        const point = taskPointCache.get(task.id)
        if (!point) return
        const tx = gx + point.x * scale
        const ty = gy + point.y * scale
        taskPx.set(task.id, { x: tx, y: ty, angle: point.angle, radius: point.radius * scale })
        if (!showSubTasks) return
        const siblings = subTasksByTaskId.get(task.id) ?? []
        siblings.forEach((subTask, subIndex) => {
          const subAngle = point.angle + ((subIndex + 1) / (siblings.length + 1) - 0.5) * subFanArc
          const subRadius = (point.radius + 32 + subGapExtra) * scale
          subTaskPx.set(subTask.id, {
            x: gx + Math.cos(subAngle) * subRadius,
            y: gy + Math.sin(subAngle) * subRadius,
          })
        })
      })
    })

    return { width, height, goalPx, taskPx, subTaskPx }
  }, [centers, columnsCount, goals, rowAbove, rowsCount, scale, showSubTasks, stageHeight, stageWidth, subFanArc, subGapExtra, subTasksByTaskId, taskPointCache, tasksByGoalId])

  const checklistPlacements = useMemo(() => {
    if (!showChecklists) return []
    return checklistItems.map((item) => {
      const direct = item.parentId ? stagePx.taskPx.get(item.parentId) ?? stagePx.subTaskPx.get(item.parentId) : undefined
      const parent = item.parentId ? elementById.get(item.parentId) : null
      const parentTaskPx = parent?.parentId ? stagePx.taskPx.get(parent.parentId) : undefined
      const rootFallback = item.rootGoalId ? stagePx.goalPx.get(item.rootGoalId) : undefined
      const base = direct ?? parentTaskPx ?? rootFallback
      if (!base || getChecklistDateMs(item) === null) return null
      const angle = hashRatio(item.id, 'angle') * TAU
      const orbit = (22 + hashRatio(item.id, 'radius') * 18) * scale
      return {
        item,
        x: base.x + Math.cos(angle) * orbit,
        y: base.y + Math.sin(angle) * orbit,
      }
    })
  }, [checklistItems, elementById, scale, showChecklists, stagePx])

  if (goals.length === 0) {
    return <div className="grid min-h-80 place-items-center text-sm text-[var(--muted)]">No constellation nodes yet.</div>
  }

  const isGoalEmphasized = (goalId: string) => {
    if (selectedElementId === goalId || hoveredGoalId === goalId) return true
    if (hoveredTaskId) {
      const parent = elementById.get(hoveredTaskId)
      if (parent?.parentId === goalId) return true
    }
    return false
  }
  const isTaskEmphasized = (taskId: string, goalId: string) =>
    selectedElementId === taskId || hoveredTaskId === taskId || isGoalEmphasized(goalId)

  return (
    <section id="constellation-view" className="grid h-full min-h-0 gap-4" style={{ gridTemplateRows: 'minmax(0, 1fr)' }}>
      <div
        ref={stageRef}
        className="relative min-h-0 overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]"
        style={{ minHeight: stageHeight, overscrollBehavior: 'contain', touchAction: 'none' }}
      >
        <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(color-mix(in_srgb,var(--text-strong)_3.5%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_srgb,var(--text-strong)_3.5%,transparent)_1px,transparent_1px)] [background-size:34px_34px]" />

        <svg
          className="pointer-events-none absolute inset-0 z-10"
          width={stagePx.width}
          height={stagePx.height}
          viewBox={`0 0 ${Math.max(1, stagePx.width)} ${stagePx.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {goals.map((goal) => {
            const gp = stagePx.goalPx.get(goal.id)
            if (!gp) return null
            const goalTasks = tasksByGoalId.get(goal.id) ?? []
            const gs = elementStartMs(goal)
            const ge = Math.max(gs + 1, elementEndMs(goal))
            const startFrac = clamp((gs - viewStartMs) / windowMs, 0, 1)
            const endFrac = clamp((ge - viewStartMs) / windowMs, 0, 1)
            const startAngle = -Math.PI / 2 + startFrac * TAU
            const endAngle = -Math.PI / 2 + endFrac * TAU
            const showArc = endFrac > startFrac + 0.001
            const todayAngle = todayContext.todayRatio !== null
              ? -Math.PI / 2 + todayContext.todayRatio * TAU
              : null
            const emphasized = isGoalEmphasized(goal.id)

            return (
              <g key={`ring-${goal.id}`}>
                <circle
                  cx={gp.x}
                  cy={gp.y}
                  r={ringRadius}
                  fill="none"
                  style={{ stroke: emphasized ? 'color-mix(in srgb, var(--info) 85%, transparent)' : 'var(--border)' }}
                  strokeOpacity={emphasized ? 0.85 : 0.55}
                  strokeWidth={emphasized ? 1.4 : 1}
                  strokeDasharray="2 5"
                />
                {showArc && (
                  <path
                    d={arcPath(gp.x, gp.y, ringRadius, startAngle, endAngle)}
                    fill="none"
                    style={{ stroke: getGoalRingStroke(goal, todayContext.todayMs) }}
                    strokeOpacity={emphasized ? 0.95 : 0.65}
                    strokeWidth={emphasized ? 3 : 2}
                    strokeLinecap="round"
                  />
                )}
                {todayAngle !== null && (
                  <g>
                    <line
                      x1={gp.x + Math.cos(todayAngle) * (ringRadius - 8)}
                      y1={gp.y + Math.sin(todayAngle) * (ringRadius - 8)}
                      x2={gp.x + Math.cos(todayAngle) * (ringRadius + (emphasized ? 16 : 12))}
                      y2={gp.y + Math.sin(todayAngle) * (ringRadius + (emphasized ? 16 : 12))}
                      style={{ stroke: 'var(--warning)' }}
                      strokeWidth={emphasized ? 3 : 2.4}
                      strokeLinecap="round"
                    />
                    <circle
                      cx={gp.x + Math.cos(todayAngle) * (ringRadius + (emphasized ? 16 : 12))}
                      cy={gp.y + Math.sin(todayAngle) * (ringRadius + (emphasized ? 16 : 12))}
                      r={emphasized ? 4.5 : 3.5}
                      style={{ fill: 'var(--warning)', stroke: 'var(--border-strong)' }}
                      strokeWidth={0.8}
                    />
                  </g>
                )}
                {goalTasks.map((task, taskIndex) => {
                  const tp = stagePx.taskPx.get(task.id)
                  if (!tp) return null
                  const color = getElementColor(task, taskIndex)
                  const midX = gp.x + (tp.x - gp.x) * 0.55
                  const midY = gp.y + (tp.y - gp.y) * 0.55
                  const taskEmphasized = isTaskEmphasized(task.id, goal.id)
                  return (
                    <g key={`conn-${task.id}`}>
                      <path
                        d={`M ${gp.x} ${gp.y} Q ${midX} ${midY} ${tp.x} ${tp.y}`}
                        fill="none"
                        stroke={color}
                        strokeOpacity={taskEmphasized ? 0.65 : 0.22}
                        strokeWidth={taskEmphasized ? 1.9 : 1.25}
                      />
                      {showSubTasks && (subTasksByTaskId.get(task.id) ?? []).map((subTask) => {
                        const sp = stagePx.subTaskPx.get(subTask.id)
                        if (!sp) return null
                        const subEmphasized = taskEmphasized || selectedElementId === subTask.id
                        return (
                          <line
                            key={`conn-${subTask.id}`}
                            x1={tp.x}
                            y1={tp.y}
                            x2={sp.x}
                            y2={sp.y}
                            stroke={color}
                            strokeOpacity={subEmphasized ? 0.55 : 0.18}
                            strokeWidth={subEmphasized ? 1.4 : 1}
                          />
                        )
                      })}
                    </g>
                  )
                })}
              </g>
            )
          })}
        </svg>


        {goals.map((goal, goalIndex) => {
          const goalTasks = tasksByGoalId.get(goal.id) ?? []
          const progress = calculateElementProgress(goal.id)
          const center = centers[goalIndex]
          const gp = stagePx.goalPx.get(goal.id)
          if (!gp) return null
          const isGoalSelected = selectedElementId === goal.id
          const gs = elementStartMs(goal)
          const ge = Math.max(gs + 1, elementEndMs(goal))
          const overlapMs = Math.max(0, Math.min(ge, viewEndMs) - Math.max(gs, viewStartMs))
          const overlapPct = ge > gs ? Math.round((overlapMs / (ge - gs)) * 100) : 0
          const goalHoverInfo = `${getHoverTitle(goal)}\n${overlapPct}% of goal range in view`
          const labelTop = clamp(gp.y - goalSize / 2 - labelReserveAbove, 8, Math.max(8, stagePx.height - 48))

          return (
            <div key={goal.id}>
              <div
                className="pointer-events-none absolute z-30 max-w-[18rem] rounded-md border border-[var(--border)] bg-[color-mix(in_srgb,var(--surface)_84%,transparent)] px-3 py-2 shadow-sm backdrop-blur-sm"
                style={{
                  left: `${clamp(center.x - 18, 3, 78)}%`,
                  top: labelTop,
                }}
              >
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">
                  {goalTasks.length} tasks · {overlapPct}% in view
                </p>
                <p className="truncate text-sm font-black text-[var(--text-strong)]">{goal.title}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
                  {formatElementRange(goal)}
                </p>
              </div>
              <motion.div
                layout
                layoutId={goal.id}
                transition={transition}
                role="button"
                tabIndex={0}
                aria-label={`Goal ${goal.title}, ${progress}% complete`}
                className={`absolute z-20 grid -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full border bg-[var(--text-strong)] text-center text-[var(--bg)] shadow-2xl transition-shadow duration-200 ${
                  isGoalSelected ? 'border-[var(--info)] ring-4 ring-[var(--info)] shadow-[var(--shadow-card)]' : 'border-[var(--border)] hover:shadow-[var(--shadow-card)]'
                }`}
                style={{
                  left: gp.x,
                  top: gp.y,
                  width: goalSize,
                  height: goalSize,
                  padding: 8 * scale,
                }}
                onClick={() => onElementClick(goal)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onElementClick(goal)
                }}
                onPointerEnter={() => setHoveredGoalId(goal.id)}
                onPointerLeave={() => setHoveredGoalId((current) => (current === goal.id ? null : current))}
                onFocus={() => setHoveredGoalId(goal.id)}
                onBlur={() => setHoveredGoalId((current) => (current === goal.id ? null : current))}
                data-hover-info={goalHoverInfo}
              >
                <div className="flex flex-col items-center justify-center overflow-hidden" style={{ maxWidth: goalSize - 12 * scale }}>
                  <p className="font-black leading-none" style={{ fontSize: `${1.15 * goalFont}rem` }}>{progress}%</p>
                  <p
                    className="mt-1 break-words font-black uppercase leading-[1.05]"
                    style={{
                      fontSize: `${0.48 * goalFont}rem`,
                      letterSpacing: '0.01em',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {compactLabel(goal.title, 22)}
                  </p>
                </div>
              </motion.div>

              {goalTasks.map((task, index) => {
                const tp = stagePx.taskPx.get(task.id)
                if (!tp) return null
                const taskProgress = calculateElementProgress(task.id)
                const taskSubs = subTasksByTaskId.get(task.id) ?? []
                const isTaskSelected = selectedElementId === task.id
                const taskColor = getElementColor(task, index)
                const rank = priorityRank.get(task.id) ?? 0.5
                const priorityRingOpacity = 0.28 + 0.62 * rank
                const priorityScale = 1 + 0.08 * rank
                const finalTaskSize = taskSize * priorityScale
                const isTaskBlocked = blockedTaskIds.has(task.id)

                return (
                  <div key={task.id}>
                    <motion.div
                      layout
                      layoutId={task.id}
                      transition={transition}
                      role="button"
                      tabIndex={0}
                      aria-label={`Task ${task.title}, ${taskProgress}% complete`}
                      className={`absolute z-30 grid -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full text-center font-black text-[var(--bg)] shadow-xl ring-4 transition-transform duration-150 ${
                        isTaskSelected ? 'ring-[var(--border-strong)] shadow-[var(--shadow-card)]' : 'ring-[var(--border-subtle)] hover:scale-110 hover:ring-[var(--border-strong)]'
                      }`}
                      style={{
                        left: tp.x,
                        top: tp.y,
                        width: finalTaskSize,
                        height: finalTaskSize,
                        fontSize: `${0.625 * goalFont}rem`,
                        backgroundColor: taskColor,
                        boxShadow: isTaskSelected ? undefined : `0 0 0 4px color-mix(in srgb, var(--text-strong) ${(priorityRingOpacity * 100).toFixed(0)}%, transparent), var(--shadow-card)`,
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        onElementClick(task)
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.stopPropagation()
                        onElementClick(task)
                      }}
                      onPointerEnter={() => {
                        setHoveredTaskId(task.id)
                        setHoveredGoalId(goal.id)
                      }}
                      onPointerLeave={() => {
                        setHoveredTaskId((current) => (current === task.id ? null : current))
                        setHoveredGoalId((current) => (current === goal.id ? null : current))
                      }}
                      onFocus={() => {
                        setHoveredTaskId(task.id)
                        setHoveredGoalId(goal.id)
                      }}
                      onBlur={() => {
                        setHoveredTaskId((current) => (current === task.id ? null : current))
                        setHoveredGoalId((current) => (current === goal.id ? null : current))
                      }}
                      data-hover-info={getHoverTitle(task)}
                    >
                      {taskProgress}%
                    </motion.div>
                    {isTaskBlocked && (
                      <span
                        className="pointer-events-none absolute z-40 -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{
                          left: tp.x + finalTaskSize * 0.32,
                          top: tp.y - finalTaskSize * 0.32,
                          width: 10 * scale,
                          height: 10 * scale,
                          backgroundColor: 'var(--danger)',
                          boxShadow: '0 0 0 1.5px var(--surface), var(--shadow-card)',
                        }}
                        aria-hidden="true"
                        data-hover-info={`Task blocked or has invalid dependencies`}
                      />
                    )}
                    {showSubTasks && taskSubs.map((subTask, subTaskIndex) => {
                      const sp = stagePx.subTaskPx.get(subTask.id)
                      if (!sp) return null
                      const isSubTaskSelected = selectedElementId === subTask.id

                      return (
                        <motion.span
                          key={subTask.id}
                          layout
                          layoutId={subTask.id}
                          transition={transition}
                          role="button"
                          tabIndex={0}
                          aria-label={`Subtask ${subTask.title}`}
                          className={`absolute z-40 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full shadow-lg ring-2 transition-transform duration-150 ${
                            isSubTaskSelected ? 'ring-[var(--border-strong)] shadow-[var(--shadow-card)]' : 'ring-[var(--border)] hover:scale-125 hover:ring-[var(--border-strong)]'
                          }`}
                          style={{
                            left: sp.x,
                            top: sp.y,
                            width: subTaskSize,
                            height: subTaskSize,
                            backgroundColor: getElementColor(subTask, subTaskIndex),
                          }}
                          onClick={(event) => {
                            event.stopPropagation()
                            onElementClick(subTask)
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return
                            event.stopPropagation()
                            onElementClick(subTask)
                          }}
                          onPointerEnter={() => {
                            setHoveredTaskId(task.id)
                            setHoveredGoalId(goal.id)
                          }}
                          onPointerLeave={() => {
                            setHoveredTaskId((current) => (current === task.id ? null : current))
                            setHoveredGoalId((current) => (current === goal.id ? null : current))
                          }}
                          data-hover-info={getHoverTitle(subTask)}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )
        })}
        {checklistPlacements.map((placement) => placement && (
          <Pressable
            key={`constellation-checklist-${placement.item.id}`}
            className={`absolute z-50 -translate-x-1/2 -translate-y-1/2 rotate-45 cursor-pointer rounded-[4px] border shadow-lg transition-transform hover:scale-125 ${
              placement.item.isCompleted ? 'border-[var(--success)] bg-[var(--success)]' : 'border-[var(--border-strong)] bg-[var(--text-strong)]'
            }`}
            style={{ left: placement.x, top: placement.y, width: checklistSize, height: checklistSize }}
            aria-label={`Open checklist ${placement.item.title}`}
            title={placement.item.title}
            data-no-dashboard-drag="true"
            data-hover-info={`${placement.item.isCompleted ? 'Done' : 'Open'} checklist: ${placement.item.title}`}
            onClick={(event) => {
              event.stopPropagation()
              onChecklistClick(placement.item.id)
            }}
          >
            <span className="sr-only">{placement.item.title}</span>
          </Pressable>
        ))}
      </div>
    </section>
  )
}
